import { Chip, Skeleton } from "@heroui/react";
import type { ReactNode } from "react";
import { Link, useLocation, type To } from "react-router";
import { pageLinkState } from "../../lib/navigation";
import { Icon } from "../Icon";
import { IngestionIcon } from "../IngestionIcon";
import { PanelStatus } from "../PanelStatus";
import { NewsActions } from "./NewsActions";
import { SourceAvatar } from "./SourceAvatar";
import type { NewsItem, TextRun } from "../../data/mock";

function Runs({ runs }: { runs: TextRun[] }) {
  return (
    <>
      {runs.map((run, i) =>
        run.strong ? (
          <span key={i} className="font-medium text-foreground">
            {run.text}
          </span>
        ) : (
          <span key={i}>{run.text}</span>
        ),
      )}
    </>
  );
}

/** The look of a source's chip (SourceChip), shared by «Rimuovi il filtro» (RemoveFilterChip). */
export const SOURCE_CHIP_CLASS =
  "h-[22px] gap-1.5 rounded-2xl bg-accent-soft px-2 py-px text-xs leading-5 font-medium text-accent";

/**
 * The chip of the organisation or of the section: the whole chip leads to
 * that source's view, in the app, which filters Ultime news on that source.
 * The funnel says so (since 20/10/2026, in place of the arrow), at the size
 * the chip's icon had.
 */
export function SourceChip({ name, url }: { name: string; url: string }) {
  return (
    <Link to={url} className="rounded-2xl">
      <Chip size="sm" className={SOURCE_CHIP_CLASS}>
        {name}
        <Icon name="funnel" className="size-3" />
      </Chip>
    </Link>
  );
}

export const REMOVE_FILTER = "Rimuovi il filtro";

/**
 * «Rimuovi il filtro», under the heading of a source's view: a source chip
 * with the funnel crossed out, that leads back to the lists of every source.
 * A link, as the chips: it changes the address, a middle click opens a tab.
 */
export function RemoveFilterChip({ to }: { to: To }) {
  return (
    <Link to={to} className="rounded-2xl">
      <Chip size="sm" className={SOURCE_CHIP_CLASS}>
        {REMOVE_FILTER}
        <Icon name="funnelXmark" className="size-3" />
      </Chip>
    </Link>
  );
}

const TEXT_LOADING = "Caricamento del testo…";
export const OPEN_PAGE = "Apri la news in pagina";
const TEXT_FAILED = "Caricamento del testo non riuscito.";

/**
 * The text of a card opened in place, when it comes from somewhere else than
 * `news.body`: on its way, failed (with «Riprova» when it can be asked
 * again), or there, with the neutral notices that stand above (or instead of)
 * `news.body`. Absent: `news.body` as it is.
 */
export type NewsCardDetail =
  | { status: "loading" }
  | { status: "error"; onRetry?: () => void }
  | { status: "ready"; notices: ReactNode[]; more?: ReactNode };

export type NewsCardProps = {
  news: NewsItem;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleSaved: () => void;
  /** "down": the card opens in place; "right": the chevron is a link to the card's own page (news.pageUrl). */
  chevron?: "down" | "right";
  detail?: NewsCardDetail;
} & NewsCardOptions;

/**
 * What a page that draws the card in its own way asks for; every option left
 * out leaves the card as the lists of news have it.
 */
export type NewsCardOptions = {
  /** false: no chip of organisation and section (a page that already says them, a sitting's). Default true. */
  showSource?: boolean;
  /** The body of the opened card, from outside: it replaces the text (`news.body`, `detail`). */
  content?: ReactNode;
  /** The row of actions under the opened card, instead of «Apri link esterno» / «Rielabora con AI» (NewsActions). */
  actions?: ReactNode;
  /** The accessible names of the chevron that opens and closes the card; default «Espandi news» / «Comprimi news». */
  expandLabel?: string;
  collapseLabel?: string;
};

/**
 * The loading text of an opened card: the excerpt's lines of the list's
 * placeholder card (NewsCardPlaceholder), named for assistive technology
 * (by default, the text; the page of a news names the whole news).
 */
export function TextLoading({ label = TEXT_LOADING }: { label?: string } = {}) {
  return (
    <div role="status" aria-label={label} className="flex flex-col">
      {["w-full", "w-full", "w-2/3"].map((width, line) => (
        <div key={line} className="py-1">
          <Skeleton className={`h-3 ${width}`} />
        </div>
      ))}
    </div>
  );
}

/**
 * The title or the text of a card as a link to the card's own page, for every
 * card that has one (since 23/10/2026); a card without a page keeps them plain.
 */
function PageLink({ news, children }: { news: NewsItem; children: ReactNode }) {
  const location = useLocation();
  if (!news.pageUrl) return <>{children}</>;
  return (
    <Link to={news.pageUrl} state={pageLinkState(location)} className="block rounded-sm hover:underline">
      {children}
    </Link>
  );
}

function Body({ news, detail }: { news: NewsItem; detail: NewsCardDetail | undefined }) {
  if (detail?.status === "loading") return <TextLoading />;
  if (detail?.status === "error") {
    return (
      <PanelStatus role="alert" indent={false} onRetry={detail.onRetry}>
        {TEXT_FAILED}
      </PanelStatus>
    );
  }
  return (
    <>
      {detail?.notices.map((notice, i) => (
        <PanelStatus key={i} role="note" indent={false}>
          {notice}
        </PanelStatus>
      ))}
      {news.body.length > 0 && (
        <PageLink news={news}>
          <div className="text-xs leading-5 text-muted wrap-anywhere">
            {news.body.map((paragraph, i) => (
              <p key={i} className="min-h-5">
                <Runs runs={paragraph} />
              </p>
            ))}
          </div>
        </PageLink>
      )}
      {detail?.status === "ready" && detail.more}
    </>
  );
}

export function NewsCard({
  news,
  expanded,
  onToggleExpanded,
  onToggleSaved,
  chevron = "down",
  detail,
  showSource = true,
  content,
  actions,
  expandLabel = "Espandi news",
  collapseLabel = "Comprimi news",
}: NewsCardProps) {
  const chevronRotation = expanded ? "-scale-y-100" : chevron === "right" ? "-rotate-90" : "";
  // the page of the news comes back here with «Torna alle news»: the list and its filters as they are now
  // (and, from a sitting's page, the sitting keeps its own way back to the sittings)
  const location = useLocation();

  return (
    <article
      className={`flex w-full flex-col gap-[25px] rounded-lg border border-slate-100 bg-surface px-[15px] pt-[13px] shadow-surface ${
        expanded ? "pb-5" : "pb-[13px]"
      }`}
    >
      <div className="flex w-full gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-[18px]">
          <div className="flex flex-col gap-[13px]">
            {showSource && (
              <div className="flex items-center gap-[9px]">
                <SourceChip {...news.source} />
                <SourceChip {...news.channel} />
                {news.hasCode && <IngestionIcon />}
              </div>
            )}
            <div className="flex items-center gap-[13px]">
              <SourceAvatar logo={news.source.logo} initials={news.source.initials} />
              <p className="text-xs leading-5 font-medium">
                <span className="block text-black">{news.heading ?? news.channel.name}</span>
                <span className="block text-muted">{news.publishedLabel}</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm leading-6 font-medium text-accent wrap-anywhere">
              <PageLink news={news}>{news.title}</PageLink>
            </h3>
            {expanded ? (
              (content ?? <Body news={news} detail={detail} />)
            ) : (
              <PageLink news={news}>
                <div className="text-xs leading-5 text-muted wrap-anywhere">
                  <p>
                    <Runs runs={news.excerpt} />
                  </p>
                </div>
              </PageLink>
            )}
          </div>
        </div>

        {/* without the bookmark (a sitting's row) the chevron keeps its place at the bottom */}
        <div className={`flex w-4 shrink-0 flex-col items-start ${news.savable === false ? "justify-end" : "justify-between"}`}>
          {news.savable !== false && (
            <button
              type="button"
              onClick={onToggleSaved}
              aria-pressed={news.saved}
              aria-label={news.saved ? "Rimuovi dalle news salvate" : "Salva news"}
              className="cursor-pointer rounded-sm"
            >
              <Icon name={news.saved ? "bookmark" : "bookmarkOutline"} />
            </button>
          )}
          {chevron === "right" ? (
            // the same chevron, turned to the right, as a link to the card's own page
            <Link
              to={news.pageUrl}
              state={pageLinkState(location)}
              aria-label={news.pageLabel ?? OPEN_PAGE}
              className="cursor-pointer rounded-sm"
            >
              <Icon name="chevronDownWide" className={`transition-transform ${chevronRotation}`} />
            </Link>
          ) : (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              aria-label={expanded ? collapseLabel : expandLabel}
              className="cursor-pointer rounded-sm"
            >
              <Icon name="chevronDownWide" className={`transition-transform ${chevronRotation}`} />
            </button>
          )}
        </div>
      </div>

      {expanded && (actions ?? <NewsActions url={news.url} />)}
    </article>
  );
}
